import { t } from "i18next";

import { Schedule } from "@mui/icons-material";
import { Box, SxProps, Theme, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import {
  getDateStringDMY,
  getTimeString,
  secondsToTimeString
} from "@/src/utils/dateUtils";

const ENDING_EMPHASIS_TIME = 300; // 5 mins

interface AuctionTimerProps {
  variant: "contained" | "outlined" | "plain";
  timeLeft: number;
  sideNote?: string;
  endTime?: string;
  sx?: SxProps<Theme>;
}

const AuctionTimer = (props: AuctionTimerProps) => {
  const timeString =
    props.timeLeft > 0
      ? secondsToTimeString(props.timeLeft, {
          daysUnit: " " + t("components.auctionTimer.daysUnit")
        })
      : "-";

  const isEndingSoon = props.timeLeft < ENDING_EMPHASIS_TIME;

  let fontColor = theme.palette.semantic.secondary.main;
  if (props.variant === "contained" && isEndingSoon) {
    fontColor = theme.palette.common.white;
  }

  let endTimeString = "";
  if (props.endTime) {
    const date = new Date(props.endTime);
    if (!isNaN(date.getTime())) {
      endTimeString = `${getDateStringDMY(props.endTime)} ${getTimeString(
        props.endTime
      )}`;
    }
  }

  let backgroundColor: string = theme.palette.background.paper;
  let separatorColor: string = theme.palette.divider;
  if (props.variant === "contained") {
    backgroundColor = isEndingSoon
      ? theme.palette.semantic.primary.main
      : theme.palette.divider;
    separatorColor = isEndingSoon
      ? theme.palette.semantic.primary.dark
      : theme.palette.text.disabled;
  }
  const outlinedBorderColor = isEndingSoon
    ? theme.palette.semantic.primary.main
    : theme.palette.semantic.secondary.main;

  if (props.variant === "plain") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          ...props.sx
        }}
      >
        <Typography
          component="span"
          sx={{
            pb: 0,
            fontFamily: "inherit",
            whiteSpace: "nowrap",

            fontSize: "inherit",
            fontWeight: "inherit",
            lineHeight: 1
          }}
        >
          {timeString}
        </Typography>
        {endTimeString && (
          <Typography
            variant="body2"
            component="span"
            sx={{ mt: 0.5, display: "block" }}
          >
            {endTimeString}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        p: 1,
        backgroundColor: backgroundColor,
        border: props.variant === "outlined" ? "1px solid" : "none",
        borderColor: outlinedBorderColor,
        borderRadius: props.variant === "outlined" ? "5rem" : "0",
        ...props.sx
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.25,
          borderRight: "1px solid",
          ...(props.sideNote
            ? { width: "57%", borderColor: separatorColor }
            : { width: "100%", border: "0" })
        }}
      >
        <Schedule
          sx={{
            ml: 0.5,
            color: isEndingSoon
              ? theme.palette.common.white
              : theme.palette.semantic.primary.main
          }}
        />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pr: 0.5
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: fontColor,
              mb: 0,
              pb: 0
            }}
          >
            {timeString}
          </Typography>
          {endTimeString && (
            <Typography
              variant="caption"
              sx={{
                color: fontColor
              }}
            >
              {endTimeString}
            </Typography>
          )}
        </Box>
      </Box>
      {props.sideNote && props.sideNote !== "" && (
        <Box
          sx={{
            width: "43%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: fontColor,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              pl: 1
            }}
          >
            {props.sideNote}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AuctionTimer;
